/**
 * @file: CacheManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { CachedFile } from '@euler/model/CachedFile';
import {
  deleteCachedFile,
  readCachedFileContents,
  writeContentsToCachedFile,
} from './cacheUtil';

export class CachedObject<T> {
  constructor(
    private readonly path: string,
    private readonly ttl: number,
    private readonly enabled = true,
  ) {
    if (this.enabled && !path) {
      this.enabled = false;
    }
  }

  async read(): Promise<{ object: T | null; expired?: boolean }> {
    if (!this.enabled) {
      if (__DEV__) {
        // delete the cache for dev
        await deleteCachedFile(this.path, true);
      }
      return { object: null };
    }
    const contents = await readCachedFileContents(this.path);
    if (!contents) {
      return { object: null };
    }
    const cached: CachedFile<T> = JSON.parse(contents);
    const expired = cached.createTime < Date.now() - this.ttl * 1000;
    if (expired && this.ttl > 0) {
      await deleteCachedFile(this.path, true);
    }
    return { object: cached.data, expired };
  }

  async write(data: T) {
    if (!this.enabled) return;
    const cached: CachedFile<T> = {
      createTime: Date.now(),
      data,
      ttl: this.ttl,
    };
    await writeContentsToCachedFile(this.path, JSON.stringify(cached, null, 2));
  }

  async delete() {
    if (!this.enabled) return;
    await deleteCachedFile(this.path, true);
  }
}
